import { useState } from 'react'
import styled from 'styled-components'
import { BulkUserManagementDiff } from 'Services/apiResponseTypes'
import { spacingMD, spacingSM } from 'Utils/styles/spacing'
import { elfGreen, frostedGlass, towerGray, white } from 'Utils/styles/colors'
import { SummaryBanner } from './SummaryBanner'
import { NewUsersTab } from './NewUsersTab'
import { UpdatesTab } from './UpdatesTab'
import { RemovalsTab } from './RemovalsTab'
import { UnchangedTab } from './UnchangedTab'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const TabContainer = styled.div`
  display: flex;
  gap: ${spacingSM};
  margin-bottom: ${spacingMD};
  border-bottom: 2px solid ${frostedGlass};
`

const Tab = styled.button<{ active: boolean }>`
  padding: ${spacingSM} ${spacingMD};
  background: ${({ active }) => (active ? elfGreen : 'transparent')};
  color: ${({ active }) => (active ? white : towerGray)};
  border: none;
  border-bottom: ${({ active }) => (active ? `2px solid ${elfGreen}` : 'none')};
  cursor: pointer;
  font-size: 14px;
  font-weight: ${({ active }) => (active ? '600' : '400')};
  transition: all 0.2s;

  &:hover {
    background: ${({ active }) => (active ? elfGreen : frostedGlass)};
  }
`

const TabContent = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

type TabType = 'new' | 'updates' | 'removals' | 'unchanged'

type DiffPreviewViewProps = {
  diff: BulkUserManagementDiff
  summary: {
    total_csv_rows: number
    new_users_count: number
    updated_users_count: number
    removed_users_count: number
    unchanged_users_count: number
    total_changes: number
  }
}

export const DiffPreviewView = ({ diff, summary }: DiffPreviewViewProps) => {
  const [activeTab, setActiveTab] = useState<TabType>('new')

  return (
    <Container>
      <h3>Preview of Changes</h3>
      <SummaryBanner summary={summary} />

      <TabContainer>
        <Tab active={activeTab === 'new'} onClick={() => setActiveTab('new')}>
          New Users ({summary.new_users_count})
        </Tab>
        <Tab active={activeTab === 'updates'} onClick={() => setActiveTab('updates')}>
          Updates ({summary.updated_users_count})
        </Tab>
        <Tab active={activeTab === 'removals'} onClick={() => setActiveTab('removals')}>
          Removals ({summary.removed_users_count})
        </Tab>
        <Tab active={activeTab === 'unchanged'} onClick={() => setActiveTab('unchanged')}>
          Unchanged ({summary.unchanged_users_count})
        </Tab>
      </TabContainer>

      <TabContent>
        {activeTab === 'new' && <NewUsersTab users={diff.new_users} />}
        {activeTab === 'updates' && <UpdatesTab users={diff.updated_users} />}
        {activeTab === 'removals' && <RemovalsTab users={diff.removed_users} />}
        {activeTab === 'unchanged' && <UnchangedTab users={diff.unchanged_users} />}
      </TabContent>
    </Container>
  )
}
